import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Check, Download, Shield, Users, BookOpen, MessageCircle,
  Heart, Calendar, ChevronRight, Sparkles, Zap, FileText, Clock,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import ProductImageCarousel from '../components/ProductImageCarousel';
import Lightbox from '../components/Lightbox';
import Breadcrumbs from '../components/Breadcrumbs';
import SocialShare from '../components/SocialShare';
import { addToRecentlyViewed } from '../components/RecentlyViewed';
import { toast } from '../hooks/use-toast';
import { getProductContent } from '../data/product-content';
import { capture } from '../lib/posthog';
import analytics from '../services/analytics';

const ICON_MAP = {
  BookOpen, MessageCircle, Heart, Check, Shield, Calendar, Users, Download,
};

/**
 * 12-section Product Detail Page per the COMMERCE SPECIFICATION:
 *   1. Hero            7. Perfect For
 *   2. Parent Problem  8. About GoodHabits Family
 *   3. Transformation  9. FAQ (≥8)
 *   4. What's Included 10. Continue Your Journey
 *   5. Preview Gallery 11. Bundle Promotion
 *   6. Why It Works    12. Newsletter
 *
 * Voice/tone content lives in /src/data/product-content.js, keyed by slug.
 * Products without an overlay entry still render the page — overlay-dependent
 * sections are simply skipped.
 */
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Phase 3: Prefer DB-driven content (product.content) — falls back to the
  // legacy hardcoded product-content.js file for anything not yet migrated.
  // Once an admin has entered ANY content for a product in the DB, that
  // takes precedence key-by-key, letting migration happen incrementally.
  const fileContent = product ? getProductContent(product.slug) : null;
  const dbContent = product?.content && Object.keys(product.content).length > 0
    ? product.content : null;

  // Merge: DB overrides win where present, file provides the rest.
  const content = React.useMemo(() => {
    if (!fileContent && !dbContent) return null;
    if (!dbContent) return fileContent;
    // Shallow merge with normalization from snake_case (DB) → camelCase (file schema)
    const merged = { ...(fileContent || {}) };
    if (dbContent.hero_headline || dbContent.hero_subheadline) {
      merged.hero = {
        ...(fileContent?.hero || {}),
        ...(dbContent.hero_headline ? { headline: dbContent.hero_headline } : {}),
        ...(dbContent.hero_subheadline ? { subheadline: dbContent.hero_subheadline } : {}),
      };
    }
    if (dbContent.problem_statement_title || dbContent.problem_statement_body) {
      merged.problem = {
        ...(fileContent?.problem || {}),
        ...(dbContent.problem_statement_title ? { title: dbContent.problem_statement_title } : {}),
        ...(dbContent.problem_statement_body ? { body: dbContent.problem_statement_body } : {}),
      };
    }
    if (dbContent.transformation_title || (dbContent.transformation_bullets?.length)) {
      merged.transformation = {
        ...(fileContent?.transformation || {}),
        ...(dbContent.transformation_title ? { title: dbContent.transformation_title } : {}),
        ...(dbContent.transformation_bullets?.length ? { bullets: dbContent.transformation_bullets } : {}),
      };
    }
    if (dbContent.whats_inside?.length) merged.whatsInside = dbContent.whats_inside;
    if (dbContent.perfect_for?.length) merged.perfectFor = { ...(fileContent?.perfectFor || {}), items: dbContent.perfect_for };
    if (dbContent.not_for) merged.perfectFor = { ...(merged.perfectFor || fileContent?.perfectFor || {}), notFor: dbContent.not_for };
    if (dbContent.faqs?.length) merged.faqs = dbContent.faqs;
    return merged;
  }, [fileContent, dbContent]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await api.getProduct(slug);
        setProduct(data);
        addToRecentlyViewed(data);
        capture(data.isBundle ? 'bundle_viewed' : 'product_viewed', {
          slug: data.slug,
          name: data.name,
          price: data.price,
          category: data.category,
          is_bundle: data.isBundle || false,
        });
        // GA4 recommended e-commerce event
        analytics.trackViewItem({
          id: data.id || data.slug,
          slug: data.slug,
          name: data.name,
          price: data.price,
          category: typeof data.category === 'string' ? data.category : (data.category?.slug || ''),
        });

        // Fetch related — same category first, then top up from other launch products to 3
        if (data.category) {
          const cat = typeof data.category === 'string' ? data.category : data.category.slug;
          const [sameCat, allActive] = await Promise.all([
            api.getProducts({ category: cat, limit: 6 }),
            api.getProducts({ limit: 24 }),
          ]);
          const sameCatList = (sameCat.products || [])
            .filter((p) => p.slug !== slug && !p.comingSoon);
          const usedSlugs = new Set([slug, ...sameCatList.map((p) => p.slug)]);
          const fallback = (allActive.products || [])
            .filter((p) => !p.comingSoon && !usedSlugs.has(p.slug));
          setRelatedProducts([...sameCatList, ...fallback].slice(0, 3));
        }
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // ── SEO: per-product <title>, meta description, OG tags, JSON-LD ──
  useEffect(() => {
    if (!product) return;
    const seo = content?.seo || {};
    const title = seo.title || `${product.name} | GoodHabits Family`;
    const description =
      seo.description ||
      product.short_description ||
      (product.description || '').slice(0, 160);
    const keywords = seo.keywords || '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/product/${product.slug}`;
    const imageAbs =
      (product.images && product.images[0]) || product.image || '';
    const ogImage = imageAbs?.startsWith('http') ? imageAbs : `${origin}${imageAbs}`;

    document.title = title;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.update || {}).forEach(([k, v]) => el.setAttribute(k, v));
    };

    upsertMeta('meta[name="description"]', {
      create: { name: 'description' }, update: { content: description },
    });
    if (keywords) {
      upsertMeta('meta[name="keywords"]', {
        create: { name: 'keywords' }, update: { content: keywords },
      });
    }
    upsertMeta('meta[property="og:title"]', {
      create: { property: 'og:title' }, update: { content: title },
    });
    upsertMeta('meta[property="og:description"]', {
      create: { property: 'og:description' }, update: { content: description },
    });
    upsertMeta('meta[property="og:type"]', {
      create: { property: 'og:type' }, update: { content: 'product' },
    });
    upsertMeta('meta[property="og:url"]', {
      create: { property: 'og:url' }, update: { content: url },
    });
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', {
        create: { property: 'og:image' }, update: { content: ogImage },
      });
    }
    upsertMeta('meta[name="twitter:card"]', {
      create: { name: 'twitter:card' }, update: { content: 'summary_large_image' },
    });

    // Canonical URL
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // JSON-LD Product structured data
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description,
      image: ogImage || undefined,
      url,
      sku: product.slug,
      brand: { '@type': 'Brand', name: 'GoodHabits Family' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.price,
        availability: product.comingSoon
          ? 'https://schema.org/PreOrder'
          : 'https://schema.org/InStock',
        url,
      },
    };
    if (product.rating && product.reviews) {
      ld.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviews,
      };
    }
    let ldEl = document.head.querySelector('script[data-pdp-ld]');
    if (!ldEl) {
      ldEl = document.createElement('script');
      ldEl.type = 'application/ld+json';
      ldEl.setAttribute('data-pdp-ld', 'true');
      document.head.appendChild(ldEl);
    }
    ldEl.textContent = JSON.stringify(ld);

    // Cleanup on unmount: reset title and remove JSON-LD
    return () => {
      const el = document.head.querySelector('script[data-pdp-ld]');
      if (el) el.remove();
    };
  }, [product, content]);

  const handleAddToCart = async () => {
    if (!product || product.comingSoon) return;
    setAdding(true);
    try {
      await addToCart(product);
      capture(product.isBundle ? 'bundle_add_to_cart' : 'product_add_to_cart', {
        slug: product.slug,
        name: product.name,
        price: product.price,
        is_bundle: product.isBundle || false,
      });
      // GA4 recommended e-commerce event
      analytics.trackAddToCart({
        id: product.id || product.slug,
        name: product.name,
        price: product.price,
      });
      toast({ title: 'Added to cart', description: product.name });
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen wrap py-16">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-100 rounded w-1/3" />
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square bg-slate-100 rounded-2xl" />
            <div className="space-y-3">
              <div className="h-6 bg-slate-100 rounded w-3/4" />
              <div className="h-12 bg-slate-100 rounded w-2/3" />
              <div className="h-24 bg-slate-100 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen wrap py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Product not found</h1>
        <Button className="mt-6" onClick={() => navigate('/shop')}>Browse all toolkits</Button>
      </div>
    );
  }

  const heroEyebrow = content?.hero?.eyebrow || product.category_name || 'Toolkit';
  const heroTagline = content?.hero?.tagline || product.short_description || product.description;
  const heroAgeRange = content?.hero?.ageRange || 'Ages 3–10';
  // Factual trust indicators replace placeholder reviews until authentic ones exist
  const trustChips = content?.hero?.trustChips || [
    'Instant Digital Download',
    'Printable PDF',
    heroAgeRange,
    'Created for Busy Parents',
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`pdp-${product.slug}`}>
      <div className="wrap py-8">
        <Breadcrumbs
          items={[
            { label: 'Shop', to: '/shop' },
            { label: product.category_name || 'Toolkits', to: product.category ? `/category/${product.category}` : '/shop' },
            { label: product.name },
          ]}
        />

        {/* ─── 1. HERO ────────────────────────────────────────────────── */}
        <section className="grid lg:grid-cols-2 gap-10 lg:gap-14 py-8 lg:py-12" data-testid="pdp-hero">
          <div className="order-2 lg:order-1">
            <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-3">
              {heroEyebrow}
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              {product.name}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{heroTagline}</p>

            {/* Factual trust indicators (replaces placeholder rating + review count) */}
            <div className="flex flex-wrap gap-2 mb-6">
              {trustChips.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-3 py-1.5 rounded-full"
                >
                  <Check className="h-3.5 w-3.5 text-teal-700" />
                  {chip}
                </span>
              ))}
            </div>

            {/* Price + CTAs */}
            <div className="bg-[#FEF9EF] rounded-2xl p-6 border-l-4 border-[#F97316] mb-6">
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-4xl font-bold text-slate-900">${Number(product.price || 0).toFixed(2)}</span>
                {product.original_price && product.original_price > product.price && (
                  <span className="text-lg text-slate-400 line-through">${Number(product.original_price).toFixed(2)}</span>
                )}
              </div>
              <p className="text-sm text-slate-600 mb-4">One-time purchase · Lifetime access · No subscription</p>

              {product.comingSoon ? (
                <Button disabled className="w-full bg-slate-100 text-slate-600 cursor-not-allowed border border-slate-200 font-medium" data-testid="coming-soon-cta">
                  <Sparkles className="h-4 w-4 mr-2 text-[#F97316]" />
                  Launching soon — join the waitlist below
                </Button>
              ) : (
                <Button
                  onClick={handleAddToCart}
                  disabled={adding}
                  className="w-full bg-[#0F766E] hover:bg-[#0B5F58] text-white font-semibold py-4 text-base rounded-xl shadow-md"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {adding ? 'Adding…' : `Add to Cart — $${Number(product.price || 0).toFixed(2)}`}
                </Button>
              )}

              {/* Concise trust row beneath price */}
              <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-orange-200/40">
                {[
                  { Icon: Download, label: 'Instant Download' },
                  { Icon: Shield, label: 'Lifetime Access' },
                  { Icon: FileText, label: 'Unlimited Printing' },
                ].map(({ Icon, label }) => (
                  <div key={label} className="flex flex-col items-center text-center gap-1">
                    <Icon className="h-4 w-4 text-[#0F766E]" />
                    <span className="text-[11px] font-semibold text-slate-700 leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <SocialShare className="mt-6" url={typeof window !== 'undefined' ? window.location.href : ''} title={product.name} />
          </div>

          <div className="order-1 lg:order-2">
            <ProductImageCarousel product={product} />
          </div>
        </section>

        {/* ─── 1.5  AT A GLANCE ─────────────────────────────────────── */}
        {content?.atAGlance && (
          <section className="py-6 lg:py-8 border-t border-slate-100" data-testid="pdp-at-a-glance">
            <div className="bg-slate-50 rounded-2xl p-5 lg:p-6">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">At a glance</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: 'Age range', value: content.atAGlance.ageRange, Icon: Users },
                  { label: 'Format', value: content.atAGlance.format, Icon: FileText },
                  { label: 'Length', value: content.atAGlance.pageCount, Icon: BookOpen },
                  { label: 'Delivery', value: content.atAGlance.delivery, Icon: Zap },
                  { label: 'Effort', value: content.atAGlance.effort, Icon: Clock },
                  { label: 'Access', value: content.atAGlance.access, Icon: Shield },
                ].map(({ label, value, Icon }) => (
                  <div key={label} className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      <Icon className="h-3.5 w-3.5 text-[#F97316]" />
                      {label}
                    </div>
                    <p className="text-sm font-medium text-slate-800 leading-snug">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── 2. PARENT PROBLEM ──────────────────────────────────────── */}
        {content?.parentProblem && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-parent-problem">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-3">A challenge we hear all the time</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{content.parentProblem.heading}</h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">{content.parentProblem.lede}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
              {content.parentProblem.situations.map((s, i) => (
                <div key={`pp-${i}`} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50">
                  <div className="w-2 h-2 rounded-full bg-[#F97316] mt-2 flex-shrink-0" />
                  <p className="text-slate-700">{s}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── 3. TRANSFORMATION ──────────────────────────────────────── */}
        {content?.transformation && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-transformation">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">What could change</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{content.transformation.heading}</h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">{content.transformation.vision}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {content.transformation.outcomes.map((o, i) => (
                <div key={`tr-${i}`} className="flex items-start gap-3 p-5 rounded-xl bg-[#FEF9EF]">
                  <Sparkles className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                  <p className="text-slate-800 font-medium">{o}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── 4. WHAT'S INCLUDED ─────────────────────────────────────── */}
        {content?.whatsIncluded && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-whats-included">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-3">What you&apos;ll get</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Everything inside the toolkit</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {content.whatsIncluded.map((item, i) => {
                const Icon = ICON_MAP[item.icon] || BookOpen;
                return (
                  <div key={`wi-${i}`} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <div className="w-12 h-12 rounded-xl bg-[#FEF9EF] flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-[#F97316]" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{item.description}</p>
                    {item.benefit && (
                      <p className="text-sm text-teal-800 leading-relaxed font-medium pt-3 border-t border-slate-100">
                        <span className="text-[10px] font-bold tracking-wider uppercase text-[#F97316] block mb-1">Why it matters</span>
                        {item.benefit}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── 4.5  WHAT HAPPENS AFTER PURCHASE ──────────────────────── */}
        {content?.afterPurchase && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-after-purchase">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">After you buy</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">What happens next</h2>
              <p className="mt-3 text-slate-600">From checkout to using it tonight, in three simple steps.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {content.afterPurchase.map((s) => (
                <div key={s.step} className="bg-white rounded-2xl p-6 border border-slate-200 relative">
                  <div className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-[#0F766E] text-white font-bold flex items-center justify-center shadow-md">
                    {s.step}
                  </div>
                  <h3 className="font-bold text-slate-900 mt-3 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── 5. PREVIEW GALLERY ─────────────────────────────────────── */}
        {product.images && product.images.length > 0 && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-preview-gallery">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">See inside</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">A look at every page</h2>
              <p className="mt-3 text-slate-600">Click any preview to open the fullscreen viewer.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl mx-auto">
              {product.images.map((img, i) => (
                <button
                  key={`gal-${img}-${i}`}
                  type="button"
                  onClick={() => { setGalleryIndex(i); setGalleryOpen(true); }}
                  aria-label={`Open preview ${i + 1} of ${product.images.length} at full size`}
                  className="aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 hover:shadow-lg transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-zoom-in"
                  data-testid={`gallery-image-${i}`}
                >
                  <img
                    src={img}
                    alt={`${product.name} — page ${i + 1} of ${product.images.length}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </section>
        )}

        <Lightbox
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          images={product.images || []}
          startIndex={galleryIndex}
          title={product.name}
        />

        {/* ─── 6. WHY IT WORKS ────────────────────────────────────────── */}
        {content?.whyItWorks && (
          <section className="py-12 lg:py-16 border-t border-slate-100 bg-[#FEF9EF] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 rounded-3xl" data-testid="pdp-why-it-works">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-3">Research-backed</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{content.whyItWorks.heading}</h2>
              </div>
              {content.whyItWorks.paragraphs.map((p, i) => (
                <p key={`wiw-${i}`} className="text-slate-700 leading-relaxed mb-4 text-base lg:text-lg">{p}</p>
              ))}
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                {content.whyItWorks.pillars.map((pl, i) => (
                  <div key={`pl-${i}`} className="bg-white rounded-xl p-5">
                    <p className="font-bold text-slate-900 mb-1">{pl.title}</p>
                    <p className="text-sm text-slate-600">{pl.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── 7. PERFECT FOR ─────────────────────────────────────────── */}
        {content?.perfectFor && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-perfect-for">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">Is this right for you?</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Perfect for…</h2>
                <p className="mt-2 text-slate-600">{content.perfectFor.ages}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {content.perfectFor.scenarios.map((s, i) => (
                  <li key={`pf-${i}`} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-teal-700 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{s}</span>
                  </li>
                ))}
              </ul>
              {content.perfectFor.notFor && (
                <div className="bg-slate-50 rounded-xl p-5 border-l-4 border-slate-300">
                  <p className="text-sm text-slate-600 italic">{content.perfectFor.notFor}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ─── 8. ABOUT GOODHABITS FAMILY ─────────────────────────────── */}
        <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-about-brand">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-3">About us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">Built by parents, for parents.</h2>
            <p className="text-slate-700 leading-relaxed text-lg mb-6">
              GoodHabits Family makes printable, research-backed parenting tools for the moments
              that matter most — not the moments you have time to read a book about. Every toolkit is
              tested with our community of 10,000+ families before it ships, and refined until it&apos;s
              calm enough to land on your fridge.
            </p>
            <a href="/about" className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:gap-2 transition-all">
              Read our full story <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </section>

        {/* ─── 9. FAQ ─────────────────────────────────────────────────── */}
        {content?.faq && content.faq.length > 0 && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-faq">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <p className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">FAQ</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Questions parents ask before they buy.</h2>
              </div>
              <div className="space-y-3">
                {content.faq.map((item, i) => (
                  <details key={`faq-${i}`} className="group rounded-xl bg-white border border-slate-200 hover:border-slate-300 transition-colors">
                    <summary className="flex items-center justify-between cursor-pointer p-5 list-none">
                      <span className="font-semibold text-slate-900 pr-4">{item.q}</span>
                      <ChevronRight className="h-5 w-5 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                    </summary>
                    <p className="px-5 pb-5 text-slate-600 leading-relaxed">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── 10. CONTINUE YOUR JOURNEY ──────────────────────────────── */}
        {relatedProducts.length > 0 && (
          <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-related">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-3">Continue your journey</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Pairs naturally with this</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((p) => (
                <ProductCard key={p.id || p.slug} product={p} />
              ))}
            </div>
          </section>
        )}

        {/* ─── 11. BUNDLE PROMOTION (journey framing, not deal-led) ──── */}
        <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-bundle">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#0F766E] to-[#0B5F58] rounded-3xl p-8 lg:p-12 text-white">
            <p className="text-sm font-semibold text-[#FED7AA] uppercase tracking-wider mb-3">
              {content?.bundleFrame?.eyebrow || 'Where this leads'}
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-5 leading-tight">
              {content?.bundleFrame?.heading ||
                'This toolkit is the foundation. The Launch Bundle is the full system.'}
            </h2>
            <p className="text-white/90 leading-relaxed mb-3 text-lg">
              {content?.bundleFrame?.lede ||
                'Most families come back for the toolkits that pair naturally with this one. The Launch Bundle sequences those next steps in the order parents actually use them — so the skills compound instead of competing for attention.'}
            </p>
            {content?.bundleFrame?.priceCompare && (
              <p className="text-sm text-white/70 mb-6 italic">{content.bundleFrame.priceCompare}</p>
            )}
            <Button
              onClick={() => {
                const label = (content?.bundleFrame?.ctaLabel || '').toLowerCase();
                const target = label.includes('complete')
                  ? '/product/complete-parenting-collection'
                  : '/product/launch-bundle';
                capture('cross_sell_click', {
                  from_slug: product?.slug,
                  to_slug: target.split('/').pop(),
                  cta_label: content?.bundleFrame?.ctaLabel,
                });
                navigate(target);
              }}
              className="bg-white text-[#0F766E] hover:bg-slate-50 font-semibold px-6 py-3 rounded-xl"
              data-testid="pdp-bundle-cta"
            >
              {content?.bundleFrame?.ctaLabel || 'See the Launch Bundle'}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </section>

        {/* ─── 12. NEWSLETTER ─────────────────────────────────────────── */}
        <section className="py-12 lg:py-16 border-t border-slate-100" data-testid="pdp-newsletter">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">Stay in the loop</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">One thoughtful email a week.</h2>
            <p className="text-slate-600 mb-6">Practical scripts, calm-down routines, and parenting reframes you can actually use. Unsubscribe any time.</p>
            <a
              href="/free-resources"
              className="inline-flex items-center gap-2 bg-[#0F766E] hover:bg-[#0B5F58] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              data-testid="pdp-newsletter-cta"
            >
              <Download className="h-4 w-4" />
              Grab the Free Starter Guide
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProductDetailPage;
